/**
 * YouTube-style progress timeline for the museum guided tour.
 *
 * Displays a fixed bottom bar with:
 *   - Scene segments (proportional to point count) with dividers
 *   - Point markers as dots within each segment
 *   - A progress fill that advances with audio playback
 *   - A hover preview showing scene thumbnail + name above the cursor
 *   - Click/tap to seek to any scene / point
 *
 * Usage:
 *   const tl = new MuseumTimeline({ guide, onSeek });
 *   tl.show();
 *   guideService.on('point:start', ({ sceneIdx, pointIdx }) => tl.updatePosition(sceneIdx, pointIdx));
 *   audioPlayer.on('timeupdate', ({ progress }) => tl.updateAudioProgress(progress));
 */
export default class MuseumTimeline {
  constructor({ guide, onSeek, container = document.body }) {
    // Sort scenes and their points exactly as MuseumGuideService does
    this._guide = guide
      .slice()
      .sort((a, b) => (a.position || 0) - (b.position || 0))
      .map(sc => ({
        ...sc,
        points: [...(sc.points || [])].sort((a, b) => (a.position || 0) - (b.position || 0)),
      }));

    this._onSeek = typeof onSeek === 'function' ? onSeek : null;
    this._container = container;

    // Each empty scene gets weight 1 so it still appears as a thin segment
    this._weights = this._guide.map(sc => Math.max(sc.points.length, 1));
    this._totalWeight = this._weights.reduce((s, w) => s + w, 0);

    // Cumulative weight before each scene (for progress offset calculation)
    let c = 0;
    this._cumul = this._weights.map(w => { const v = c; c += w; return v; });

    this._sceneIdx = 0;
    this._pointIdx = 0;
    this._audioProgress = 0;

    this._el = null;
    this._fillEl = null;
    this._previewEl = null;
    this._previewImg = null;
    this._previewLabel = null;
    this._trackEl = null;
    this._hideTimer = null;

    this._build();
    this._bindEvents();
  }

  // ─── public API ───────────────────────────────────────────────────────────

  updatePosition(sceneIdx, pointIdx) {
    this._sceneIdx = sceneIdx;
    this._pointIdx = pointIdx;
    this._audioProgress = 0;
    this._updateFill();
  }

  updateAudioProgress(progress) {
    this._audioProgress = progress;
    this._updateFill();
  }

  show() {
    if (!this._el) return;
    this._el.classList.add('museum-tl--visible');
    this._scheduleHide();
  }

  hide() {
    if (!this._el) return;
    this._el.classList.remove('museum-tl--visible');
    clearTimeout(this._hideTimer);
  }

  destroy() {
    clearTimeout(this._hideTimer);
    if (this._el && this._el.parentNode) {
      this._el.parentNode.removeChild(this._el);
    }
    this._el = null;
  }

  // ─── build ────────────────────────────────────────────────────────────────

  _build() {
    const el = document.createElement('div');
    el.className = 'museum-tl';

    // Hover preview (thumbnail + label), positioned above track
    const preview = document.createElement('div');
    preview.className = 'museum-tl__preview';

    const previewImg = document.createElement('img');
    previewImg.className = 'museum-tl__preview-thumb';
    previewImg.alt = '';

    const previewLabel = document.createElement('span');
    previewLabel.className = 'museum-tl__preview-label';

    preview.appendChild(previewImg);
    preview.appendChild(previewLabel);
    el.appendChild(preview);

    // Track
    const track = document.createElement('div');
    track.className = 'museum-tl__track';

    // Background bar
    const bg = document.createElement('div');
    bg.className = 'museum-tl__bg';
    track.appendChild(bg);

    // Progress fill
    const fill = document.createElement('div');
    fill.className = 'museum-tl__fill';
    track.appendChild(fill);

    // Scene segments + point markers
    this._guide.forEach((scene, si) => {
      const widthPct = (this._weights[si] / this._totalWeight) * 100;
      const leftPct  = (this._cumul[si]  / this._totalWeight) * 100;

      const seg = document.createElement('div');
      seg.className = 'museum-tl__segment';
      seg.style.left  = `${leftPct}%`;
      seg.style.width = `${widthPct}%`;
      seg.dataset.sceneIdx = si;

      // Scene divider (not on first segment — marks the left boundary of each scene)
      if (si > 0) {
        const div = document.createElement('div');
        div.className = 'museum-tl__divider';
        track.appendChild(div);
        div.style.left = `${leftPct}%`;
      }

      // Point markers: skip pi=0 (divider/bar-start already marks the scene beginning).
      // Remaining dots sit at zone-start positions so the hover label changes exactly when you reach the dot.
      scene.points.forEach((pt, pi) => {
        if (pi === 0) return;
        const n = scene.points.length;
        const marker = document.createElement('div');
        marker.className = 'museum-tl__point';
        marker.style.left = `${(pi / n) * 100}%`;
        marker.dataset.sceneIdx = si;
        marker.dataset.pointIdx = pi;
        seg.appendChild(marker);
      });

      track.appendChild(seg);
    });

    el.appendChild(track);
    this._container.appendChild(el);

    this._el = el;
    this._fillEl = fill;
    this._previewEl = preview;
    this._previewImg = previewImg;
    this._previewLabel = previewLabel;
    this._trackEl = track;

    this._updateFill();
  }

  _bindEvents() {
    if (!this._trackEl) return;

    this._trackEl.addEventListener('mousemove', (e) => this._onMouseMove(e));
    this._trackEl.addEventListener('mouseleave', () => this._hidePreview());
    this._trackEl.addEventListener('click', (e) => this._onClick(e));

    // Touch support — show timeline + seek on tap
    this._trackEl.addEventListener('touchstart', (e) => {
      this.show();
      this._onMouseMove(e.touches[0]);
    }, { passive: true });
    this._trackEl.addEventListener('touchend', (e) => {
      if (e.changedTouches[0]) this._onClick(e.changedTouches[0]);
      this._hidePreview();
    });

    // Keep visible while hovering
    this._el.addEventListener('mouseenter', () => clearTimeout(this._hideTimer));
    this._el.addEventListener('mouseleave', () => this._scheduleHide());
  }

  // ─── progress ─────────────────────────────────────────────────────────────

  _updateFill() {
    if (!this._fillEl) return;
    const progress = this._getProgress(this._sceneIdx, this._pointIdx, this._audioProgress);
    this._fillEl.style.width = `${Math.min(progress * 100, 100)}%`;
  }

  _getProgress(si, pi, audioProgress) {
    if (this._totalWeight === 0) return 0;
    const w = this._weights[si] || 1;
    const n = this._guide[si]?.points.length || 0;
    let segFrac;
    if (n <= 1) {
      segFrac = audioProgress;
    } else {
      segFrac = (pi + audioProgress) / n;
    }
    return (this._cumul[si] + segFrac * w) / this._totalWeight;
  }

  // ─── hover preview ────────────────────────────────────────────────────────

  _onMouseMove(e) {
    if (!this._trackEl || !this._previewEl) return;
    this.show();
    clearTimeout(this._hideTimer);

    const rect = this._trackEl.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));

    const { si, pi } = this._ratioToIndex(ratio);
    const scene = this._guide[si];
    if (!scene) return;

    const leftPx = e.clientX - rect.left;
    this._previewEl.style.left = `${leftPx}px`;
    this._previewEl.style.display = 'flex';

    if (scene.thumbnail) {
      this._previewImg.src = scene.thumbnail;
      this._previewImg.style.display = 'block';
    } else {
      this._previewImg.style.display = 'none';
    }

    const pointHint = (scene.points.length > 0 && pi < scene.points.length)
      ? ` · ${pi + 1}/${scene.points.length}`
      : '';
    this._previewLabel.textContent = `${scene.name || ''}${pointHint}`;
  }

  _hidePreview() {
    if (this._previewEl) this._previewEl.style.display = 'none';
  }

  // ─── click/seek ───────────────────────────────────────────────────────────

  _onClick(e) {
    if (!this._onSeek || !this._trackEl) return;
    const rect = this._trackEl.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const { si, pi } = this._ratioToIndex(ratio);
    this._onSeek(si, pi);
  }

  // ─── helpers ──────────────────────────────────────────────────────────────

  /** Convert a 0-1 track ratio to { si (sceneIdx), pi (pointIdx) } */
  _ratioToIndex(ratio) {
    let accum = 0;
    for (let si = 0; si < this._guide.length; si++) {
      const segWidth = this._weights[si] / this._totalWeight;
      if (ratio <= accum + segWidth || si === this._guide.length - 1) {
        const scene = this._guide[si];
        if (!scene.points.length) return { si, pi: 0 };
        const segRatio = Math.max(0, Math.min(1, (ratio - accum) / segWidth));
        const n = scene.points.length;
        if (n <= 1) return { si, pi: 0 };
        const pi = Math.min(Math.floor(segRatio * n), n - 1);
        return { si, pi };
      }
      accum += segWidth;
    }
    return { si: 0, pi: 0 };
  }

  _scheduleHide() {
    clearTimeout(this._hideTimer);
    this._hideTimer = setTimeout(() => {
      this._el?.classList.remove('museum-tl--visible');
    }, 4000);
  }
}
